import { isActivityFormValues } from './schemas'
import type {
  ActivityFormValues,
} from './values'
import type { CitySuggestion, PlaceSuggestion } from './types'

const DRAFT_VERSION = 1
const DRAFT_PREFIX = 'kuniri.activity-draft.v1'

export interface StoredLocation {
  selectedCity: CitySuggestion | null
  selectedPlace: PlaceSuggestion | null
  manualCity: boolean
  manualPlace: boolean
}

export interface ActivityDraft {
  values: ActivityFormValues
  location: StoredLocation
  savedAt: string
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function citySuggestion(value: unknown): CitySuggestion | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  if (
    typeof record.city !== 'string' ||
    typeof record.label !== 'string' ||
    !isFiniteNumber(record.latitude) ||
    !isFiniteNumber(record.longitude)
  ) {
    return null
  }
  return {
    city: record.city,
    label: record.label,
    latitude: record.latitude,
    longitude: record.longitude,
  }
}

function placeSuggestion(value: unknown): PlaceSuggestion | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  if (
    typeof record.label !== 'string' ||
    !isFiniteNumber(record.latitude) ||
    !isFiniteNumber(record.longitude)
  ) {
    return null
  }
  return {
    label: record.label,
    latitude: record.latitude,
    longitude: record.longitude,
  }
}

function sanitizeLocation(value: unknown): StoredLocation {
  if (!value || typeof value !== 'object') {
    return {
      selectedCity: null,
      selectedPlace: null,
      manualCity: false,
      manualPlace: false,
    }
  }
  const record = value as Record<string, unknown>
  return {
    selectedCity: citySuggestion(record.selectedCity),
    selectedPlace: placeSuggestion(record.selectedPlace),
    manualCity: record.manualCity === true,
    manualPlace: record.manualPlace === true,
  }
}

export function draftStorageKey(userId: string | null | undefined): string {
  return `${DRAFT_PREFIX}.${userId ?? 'anonymous'}`
}

/**
 * Create drafts are tab-scoped and user-scoped. They intentionally use
 * `sessionStorage`: the frozen API has no draft-creation field, and a local
 * draft must not survive as an authenticated artifact after the tab closes.
 */
export function loadDraft(
  userId: string | null | undefined,
): ActivityDraft | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(draftStorageKey(userId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    const record = parsed as Record<string, unknown>
    if (record.version !== DRAFT_VERSION) return null
    if (!isActivityFormValues(record.values)) return null
    return {
      values: record.values,
      location: sanitizeLocation(record.location),
      savedAt:
        typeof record.savedAt === 'string'
          ? record.savedAt
          : new Date().toISOString(),
    }
  } catch {
    return null
  }
}

export function saveDraft(
  userId: string | null | undefined,
  draft: Omit<ActivityDraft, 'savedAt'>,
): boolean {
  if (typeof window === 'undefined') return false
  try {
    window.sessionStorage.setItem(
      draftStorageKey(userId),
      JSON.stringify({
        version: DRAFT_VERSION,
        savedAt: new Date().toISOString(),
        values: draft.values,
        location: draft.location,
      }),
    )
    return true
  } catch {
    return false
  }
}

export function clearDraft(userId: string | null | undefined): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(draftStorageKey(userId))
  } catch {
    // Storage can be denied in private mode; the in-memory form still works.
  }
}
